import type { LegContentRecord } from '~/libs/dato/fragments/legContentFragment';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { TripOverviewItem, TripOverviewLeg, TripOverviewStay } from '~/services/engine-ts';
import type { AnyJourneyItemTransformed, LegTransformed, StayTransformed, Transport } from '~/types/JourneyItem.types';
import { getOvernightTransportMode } from '~/utils/transportModeHelper';

type CmsHeroLeg = HeroRouteRecord['legs'][number];

export type LegTerminals = {
  originTerminalName: string | null;
  destinationTerminalName: string | null;
  isFirstClass: boolean;
  transportClassLabel: string | null;
  channel: string | null;
};

export type LegTerminalsByItemId = ReadonlyMap<number, LegTerminals>;

export type TransformOverviewItemsOptions = {
  terminals?: LegTerminalsByItemId;
  mergeLegRuns?: boolean;
  cmsPlaces?: Record<string, PlaceImageRecord>;
  legContent?: Record<string, LegContentRecord>;
};

export function legTransportMode(leg: TripOverviewLeg): Transport {
  if (!leg.transportMode) return leg.startDate === leg.endDate ? 'JOURNEY' : 'SLEEPER';
  return (
    leg.startDate === leg.endDate ? leg.transportMode : getOvernightTransportMode(leg.transportMode)
  ) as Transport;
}

function parseDurationMinutes(departureTime: string | null, arrivalTime: string | null): number {
  if (!departureTime || !arrivalTime) return 0;
  const [dh, dm] = departureTime.split(':').map(Number);
  const [ah, am] = arrivalTime.split(':').map(Number);
  const departure = dh * 60 + dm;
  let arrival = ah * 60 + am;
  if (arrival < departure) arrival += 24 * 60;
  return arrival - departure;
}

// Overview leg ids are JAI leg ids with an "S" prefix (e.g. "S937860620"), while the CMS
// stores comma-separated bare numbers in defaultJaiLegIds / alternativePaths[].legs[].jaiLegIds.
export function jaiLegIdFromOverviewLegId(legId: string): string {
  return legId.startsWith('S') ? legId.slice(1) : legId;
}

function cmsJaiLegIdsInclude(cmsJaiLegIds: string, jaiLegId: string): boolean {
  return cmsJaiLegIds.split(',').some((id) => id.trim() === jaiLegId);
}

function findCmsHeroLeg(cmsRoute: HeroRouteRecord | undefined, legId: string): CmsHeroLeg | undefined {
  const jaiLegId = jaiLegIdFromOverviewLegId(legId);
  return cmsRoute?.legs.find(
    (cmsLeg) =>
      cmsJaiLegIdsInclude(cmsLeg.defaultJaiLegIds, jaiLegId) ||
      cmsLeg.alternativePaths.some((path) =>
        path.legs.some((alternative) => cmsJaiLegIdsInclude(alternative.jaiLegIds, jaiLegId))
      )
  );
}

function cmsHeroLegImages(cmsLegs: CmsHeroLeg[]): Array<{ src: string }> {
  return cmsLegs.flatMap((cmsLeg) =>
    cmsLeg.image?.responsiveImage ? [{ src: cmsLeg.image.responsiveImage.src }] : []
  );
}

function featuredLegDescription(item: TripOverviewLeg, cmsLegContent: LegContentRecord | undefined): string {
  if (!item.featured) return '';
  if (!cmsLegContent) return item.description;
  return cmsLegContent.description?.trim() ?? '';
}

function featuredLegImages(item: TripOverviewLeg, cmsLegContent: LegContentRecord | undefined): Array<{ src: string }> {
  if (!item.featured) return [];
  const cmsSrc = cmsLegContent?.image?.responsiveImage?.src;
  return cmsSrc ? [{ src: cmsSrc }] : item.images;
}

function transformLeg(
  item: TripOverviewLeg,
  cmsRoute: HeroRouteRecord | undefined,
  terminals?: LegTerminalsByItemId,
  legContent?: Record<string, LegContentRecord>
): LegTransformed {
  const cmsLeg = findCmsHeroLeg(cmsRoute, item.id);
  const cmsImages = cmsHeroLegImages(cmsLeg ? [cmsLeg] : (cmsRoute?.legs ?? []));
  const legTerminals = terminals?.get(item.itemId);
  const cmsLegContent = item.featured ? legContent?.[jaiLegIdFromOverviewLegId(item.id)] : undefined;
  const description = legContent
    ? featuredLegDescription(item, cmsLegContent)
    : cmsLeg?.description || cmsRoute?.description || item.description;
  const fallbackImages = cmsImages.length > 0 ? cmsImages : item.images;

  return {
    type: 'Journey',
    id: String(item.itemId),
    itemId: item.itemId,
    startDate: item.startDate,
    endDate: item.endDate,
    isFirstItem: item.isFirstItem,
    isLastItem: item.isLastItem,
    origin: item.origin.name,
    originId: item.origin.id,
    originGeoCoordinates: item.origin.geoCoordinates,
    destination: item.destination.name,
    destinationId: item.destination.id,
    destinationGeoCoordinates: item.destination.geoCoordinates,
    departureTimeText: item.departureTime,
    arrivalTimeText: item.arrivalTime,
    duration: parseDurationMinutes(item.departureTime, item.arrivalTime),
    nights: 0,
    images: legContent ? featuredLegImages(item, cmsLegContent) : fallbackImages,
    description,
    heroRouteCmsId: item.featuredRouteCmsId,
    heroRouteTitle: cmsRoute?.title ?? null,
    ticketsIncluded: item.ticketsIncluded,
    modeOfTransports: [legTransportMode(item)],
    viaStops: [],
    fromTerminalId: '',
    fromTerminalName: legTerminals?.originTerminalName ?? null,
    fromTerminalGeoCoordinates: null,
    toTerminalId: '',
    toTerminalName: legTerminals?.destinationTerminalName ?? null,
    toTerminalGeoCoordinates: null,
    body: [],
    operator: null,
    transportClass: legTerminals?.isFirstClass ? 'FIRST_CLASS' : 'STANDARD',
    transportClassLabel: legTerminals?.transportClassLabel ?? null,
    channel: legTerminals?.channel ?? null,
    interrail: item.ticketsIncluded ?? false,
    carbonFootprint: 0
  };
}

/**
 * Collapses a run of consecutive legs that share the same featured-route id into one card
 * item: first origin → last destination, every city in between as viaStops, one transport mode
 * entry per leg in order, and "tickets included" only when every leg is included. Hero runs also
 * fold the run's CMS leg images into one gallery and use the route-level description; a run
 * of plain (non-featured) legs has no cmsRoute and keeps the legs' own images/description.
 */
function transformLegRun(
  legs: TripOverviewLeg[],
  cmsRoute: HeroRouteRecord | undefined,
  terminals?: LegTerminalsByItemId
): LegTransformed {
  const first = legs[0];
  const last = legs.at(-1);
  if (legs.length === 1 || !last) return transformLeg(first, cmsRoute, terminals);

  const matchedCmsLegs = [...new Set(legs.flatMap((leg) => findCmsHeroLeg(cmsRoute, leg.id) ?? []))];
  const cmsImages = cmsHeroLegImages(matchedCmsLegs.length > 0 ? matchedCmsLegs : (cmsRoute?.legs ?? []));

  return {
    ...transformLeg(first, cmsRoute, terminals),
    id: legs.map((leg) => leg.itemId).join('-'),
    toTerminalName: terminals?.get(last.itemId)?.destinationTerminalName ?? null,
    endDate: last.endDate,
    isLastItem: last.isLastItem,
    destination: last.destination.name,
    destinationId: last.destination.id,
    destinationGeoCoordinates: last.destination.geoCoordinates,
    viaStops: legs.slice(0, -1).map((leg) => leg.destination.name),
    arrivalTimeText: last.arrivalTime,
    duration: legs.reduce((total, leg) => total + parseDurationMinutes(leg.departureTime, leg.arrivalTime), 0),
    images: cmsImages.length > 0 ? cmsImages : legs.flatMap((leg) => leg.images),
    description: cmsRoute?.description ?? first.description,
    ticketsIncluded: legs.every((leg) => leg.ticketsIncluded),
    interrail: legs.every((leg) => leg.ticketsIncluded),
    modeOfTransports: legs.map(legTransportMode)
  };
}

function cmsPlaceImages(cmsPlace: PlaceImageRecord | undefined): Array<{ src: string }> {
  return (cmsPlace?.images ?? []).flatMap((image) =>
    image?.responsiveImage?.src ? [{ src: image.responsiveImage.src }] : []
  );
}

function transformStay(item: TripOverviewStay, cmsPlace: PlaceImageRecord | undefined): StayTransformed {
  const cmsImages = cmsPlaceImages(cmsPlace);
  const description = cmsPlace?.description?.trim();

  return {
    type: 'Stay',
    id: String(item.itemId),
    stayItemId: item.itemId,
    startDate: item.startDate,
    endDate: item.endDate,
    title: item.place.name,
    nights: item.nights,
    geoCoordinates: item.place.geoCoordinates,
    images: cmsImages.length > 0 ? cmsImages : item.place.images,
    body: description ? [{ id: `${item.itemId}-description`, type: 'text', text: description }] : [],
    carbonFootprint: 0,
    country: { countryId: '', countryName: '', cmsId: '' },
    accommodationIncluded: item.accommodationIncluded ?? false
  };
}

// The trip's final leg is the return journey: it renders as a plain journey card
// (see isReturnLeg in ItineraryItemCard), so it never joins a hero-route run.
function findReturnLegIndex(visibleItems: Array<TripOverviewLeg | TripOverviewStay>): number {
  for (let index = visibleItems.length - 1; index >= 0; index--) {
    if (visibleItems[index].type === 'leg') return index;
  }
  return -1;
}

export function transformOverviewItems(
  items: TripOverviewItem[],
  cmsRoutes?: Record<string, HeroRouteRecord>,
  options?: TransformOverviewItemsOptions
): AnyJourneyItemTransformed[] {
  const { terminals, mergeLegRuns = true, cmsPlaces, legContent } = options ?? {};
  const visibleItems = items.filter((item): item is TripOverviewLeg | TripOverviewStay => item.type !== 'stop');

  const returnLegIndex = findReturnLegIndex(visibleItems);

  const transformed: AnyJourneyItemTransformed[] = [];
  let index = 0;
  while (index < visibleItems.length) {
    const item = visibleItems[index];
    if (item.type === 'stay') {
      transformed.push(transformStay(item, item.place.cmsId ? cmsPlaces?.[item.place.cmsId] : undefined));
      index += 1;
      continue;
    }

    const heroRouteCmsId = item.featuredRouteCmsId;
    const cmsRoute = heroRouteCmsId === null ? undefined : cmsRoutes?.[heroRouteCmsId];
    if (index === returnLegIndex) {
      transformed.push(transformLeg(item, cmsRoute, terminals, legContent));
      index += 1;
      continue;
    }

    if (!mergeLegRuns) {
      transformed.push(transformLeg(item, cmsRoute, terminals, legContent));
      index += 1;
      continue;
    }

    // Merge consecutive legs that share the same featured-route id into one card. Plain,
    // non-featured legs (featuredRouteCmsId === null) merge with each other the same way,
    // so a run of them between two stays renders as a single leg card.
    const run: TripOverviewLeg[] = [item];
    let next = index + 1;
    while (next < visibleItems.length && next !== returnLegIndex) {
      const candidate = visibleItems[next];
      if (candidate.type !== 'leg' || candidate.featuredRouteCmsId !== heroRouteCmsId) break;
      run.push(candidate);
      next += 1;
    }

    transformed.push(transformLegRun(run, cmsRoute, terminals));
    index = next;
  }

  return transformed;
}
